const MONTH_LABELS = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

const REQUEST_TYPE_LABEL = {
  vacation: "Vacaciones",
  personal_leave: "Permiso personal",
  sick_leave: "Baja / enfermedad",
  other_absence: "Otra ausencia",
  remote_day: "Teletrabajo",
};

const STATUS_LABEL = {
  pending: "Pendiente",
  approved: "Aprobada",
  rejected: "Rechazada",
  cancelled: "Cancelada",
};

function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInYear(year) {
  return isLeapYear(year) ? 366 : 365;
}

function parseDateOnly(value) {
  if (!value) return null;
  const s = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

function dateOnlyToUtcMs(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function dayOfYear(dateStr, year) {
  const parsed = parseDateOnly(dateStr);
  if (!parsed) return 0;
  const ms = dateOnlyToUtcMs(parsed);
  const start = Date.UTC(year, 0, 1);
  return Math.floor((ms - start) / 86400000);
}

function clipRangeToYear(startDate, endDate, year) {
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const start = startDate < yearStart ? yearStart : startDate;
  const end = endDate > yearEnd ? yearEnd : endDate;
  if (end < start) return null;
  return { start, end };
}

function countDaysInclusive(startDate, endDate) {
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (!start || !end) return 0;
  const diff = dateOnlyToUtcMs(end) - dateOnlyToUtcMs(start);
  return Math.floor(diff / 86400000) + 1;
}

function barStyleForRange(startDate, endDate, year) {
  const clipped = clipRangeToYear(startDate, endDate, year);
  if (!clipped) return null;

  const total = daysInYear(year);
  const startDay = dayOfYear(clipped.start, year);
  const endDay = dayOfYear(clipped.end, year);
  const span = endDay - startDay + 1;

  return {
    left: `${(startDay / total) * 100}%`,
    width: `${(span / total) * 100}%`,
  };
}

function monthStartDate(year, monthIndex) {
  const m = String(monthIndex + 1).padStart(2, "0");
  return `${year}-${m}-01`;
}

function monthEndDate(year, monthIndex) {
  const last = new Date(year, monthIndex + 1, 0).getDate();
  const m = String(monthIndex + 1).padStart(2, "0");
  const d = String(last).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

function clipRangeToMonth(startDate, endDate, year, monthIndex) {
  const mStart = monthStartDate(year, monthIndex);
  const mEnd = monthEndDate(year, monthIndex);
  const start = startDate < mStart ? mStart : startDate;
  const end = endDate > mEnd ? mEnd : endDate;
  if (end < start) return null;
  return { start, end };
}

function formatDayLabel(dateStr) {
  const d = parseDateOnly(dateStr);
  if (!d) return "";
  return String(Number(d.slice(8, 10)));
}

function formatRangeLabel(startDate, endDate) {
  const s = parseDateOnly(startDate);
  const e = parseDateOnly(endDate);
  if (!s || !e) return "";
  const sd = formatDayLabel(s);
  const ed = formatDayLabel(e);
  return sd === ed ? sd : `${sd}-${ed}`;
}

function entriesForEmployee(entries, userId) {
  return entries.filter((e) => Number(e.userId) === Number(userId));
}

function monthCellText(entries, year, monthIndex) {
  const parts = entries
    .map((entry) => {
      const clipped = clipRangeToMonth(
        entry.startDate,
        entry.endDate,
        year,
        monthIndex,
      );
      if (!clipped) return null;
      const label = formatRangeLabel(clipped.start, clipped.end);
      const suffix = entry.isHalfDay ? " (½)" : "";
      return label + suffix;
    })
    .filter(Boolean);
  return parts.join(", ");
}

function totalDaysForEmployee(entries, year) {
  return entries.reduce((sum, entry) => {
    const clipped = clipRangeToYear(entry.startDate, entry.endDate, year);
    if (!clipped) return sum;
    const days = countDaysInclusive(clipped.start, clipped.end);
    return sum + (entry.isHalfDay ? days * 0.5 : days);
  }, 0);
}

function buildExportDetailRows(data) {
  const { year, entries } = data;
  return entries.map((e) => ({
    Empleado: e.username || e.userId,
    Tipo: REQUEST_TYPE_LABEL[e.requestType] || e.requestType,
    Desde: e.startDate,
    Hasta: e.endDate,
    Días: e.isHalfDay
      ? countDaysInclusive(e.startDate, e.endDate) * 0.5
      : countDaysInclusive(e.startDate, e.endDate),
    "Medio día": e.isHalfDay ? e.halfDayPeriod || "Sí" : "No",
    Estado: STATUS_LABEL[e.status] || e.status,
    Motivo: e.reason || "",
  }));
}

function buildExportMatrixRows(data) {
  const { year, employees, entries } = data;
  return employees.map((emp) => {
    const empEntries = entriesForEmployee(entries, emp.id);
    const row = { Empleado: emp.username };
    MONTH_LABELS.forEach((label, idx) => {
      row[label] = monthCellText(empEntries, year, idx);
    });
    row["Total días"] = totalDaysForEmployee(empEntries, year);
    return row;
  });
}

function formatShortDateRange(startDate, endDate) {
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (!start || !end) return "";
  const sm = Number(start.slice(5, 7)) - 1;
  const em = Number(end.slice(5, 7)) - 1;
  const sd = Number(start.slice(8, 10));
  const ed = Number(end.slice(8, 10));
  if (start === end) return `${sd} ${MONTH_LABELS[sm]}`;
  if (sm === em) return `${sd}–${ed} ${MONTH_LABELS[sm]}`;
  return `${sd} ${MONTH_LABELS[sm]} – ${ed} ${MONTH_LABELS[em]}`;
}

function todayPositionInYear(year) {
  const now = new Date();
  if (now.getFullYear() !== year) return null;
  const month = now.getMonth();
  const day = now.getDate();
  const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const total = daysInYear(year);
  const idx = dayOfYear(dateStr, year);
  return ((idx + 0.5) / total) * 100;
}

function employeeInitials(username) {
  if (!username) return "?";
  const parts = String(username).trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return username.slice(0, 2).toUpperCase();
}

export {
  MONTH_LABELS,
  REQUEST_TYPE_LABEL,
  STATUS_LABEL,
  daysInYear,
  barStyleForRange,
  monthCellText,
  totalDaysForEmployee,
  entriesForEmployee,
  countDaysInclusive,
  buildExportDetailRows,
  buildExportMatrixRows,
  clipRangeToYear,
  formatShortDateRange,
  todayPositionInYear,
  employeeInitials,
};
